import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import MatrixFeed from './MatrixFeed';
import FeatureSection from './FeatureSection';
import ProductShowcase from './ProductShowcase';
import { User, Layers, ShieldCheck, Users, BrainCircuit } from 'lucide-react';

import SiteFooter from './SiteFooter'; 
import PricingSection from './PricingSection'; 
import CookieConsent from './CookieConsent'; 

export const PublicHomeWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [internalPage, setInternalPage] = useState('home');

  const { data: settings } = useQuery({
    queryKey: ['publicCms'],
    queryFn: () => axiosClient.get('/public/cms/content'),
  });

  const { data: publicStats } = useQuery({
    queryKey: ['publicStats'],
    queryFn: () => axiosClient.get('/public/stats'),
  });

  const { data: features } = useQuery({
    queryKey: ['publicFeatures'],
    queryFn: () => axiosClient.get('/public/cms/features'),
  });

  const { data: products } = useQuery({
    queryKey: ['publicProducts'],
    queryFn: () => axiosClient.get('/public/cms/product-sections'),
  });

  const { data: publicPackages } = useQuery({
    queryKey: ['publicPricing'],
    queryFn: () => axiosClient.get('/public/pricing'),
  });
    
  const handleGlobalNavigation = (key) => {
    const functionalPages = ['docs', 'download', 'contact', 'changelog', 'blog'];
    const cmsPages = ['about-us', 'careers', 'partners', 'privacy-policy', 'terms-conditions', 'cookie-policy', 'gdpr-compliance', 'refund-policy'];

    if (key === 'home') {
        setInternalPage('home');
        navigate('/'); // Updates address bar to /
    } else if (key === 'product') {
        setInternalPage('product');
        navigate('/features'); // Updates address bar to /features
    } else if (key === 'pricing') {
        setInternalPage('pricing');
        navigate('/pricing'); // Updates address bar to /pricing
    } else if (functionalPages.includes(key)) {
        navigate('/' + key);
    } else if (cmsPages.includes(key)) {
        navigate('/pages/' + key);
    }
    window.scrollTo(0, 0);
  };

  const formatLargeNumber = (num) => {
    if (!num) return '0';

    if (num >= 1_000_000_000_000) {
      return (num / 1_000_000_000_000).toFixed(1) + 'T+';
    }

    if (num >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(1) + 'B+';
    }

    if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1) + 'M+';
    }

    if (num >= 1_000) {
      return (num / 1_000).toFixed(1) + 'K+';
    }

    return num.toString();
  };

  if (!settings) return (
    <div className="bg-[#0b0f19] h-screen flex items-center justify-center text-slate-500 font-mono">
      Initializing Sentinel...
    </div>
  );

  const isRoot = location.pathname === '/';

  return (
    <div className="bg-[#0b0f19] min-h-screen flex flex-col text-white font-sans overflow-x-hidden">
      
      {/* ADVANCED PROFESSIONAL NAVBAR (Transplanted from Preview) */}
      <div className="h-20 border-b border-white/5 flex items-center px-4 md:px-8 justify-between sticky top-0 bg-[#0b0f19]/90 backdrop-blur-xl z-50">
        
        {/* 1. Left: Logo & Title */}
        <div 
          className="flex items-center flex-shrink-0 cursor-pointer group" 
          onClick={() => handleGlobalNavigation('home')}
        >
          {settings.site_logo ? (
            <img src={settings.site_logo} alt="logo" className="h-9 md:h-10 mr-3 object-contain transition-transform group-hover:scale-105" />
          ) : (
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg mr-3 flex items-center justify-center font-bold text-white shadow-lg shadow-blue-500/20">A</div>
          )}
          <span className="font-bold text-lg md:text-xl tracking-tight hidden sm:block">
            {settings.site_title || 'AIRS'} <span className="text-teal-500">Security</span>
          </span>
        </div>

        {/* 2. Center: Navigation Menu (Hidden on very small screens) */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
          <span 
            onClick={() => handleGlobalNavigation('home')} 
            className={`cursor-pointer transition-colors hover:text-white ${isRoot && internalPage === 'home' ? 'text-teal-400 font-bold' : ''}`}
          >
            Home
          </span>
          <span 
            onClick={() => handleGlobalNavigation('product')} 
            className={`cursor-pointer transition-colors hover:text-white ${isRoot && internalPage === 'product' ? 'text-teal-400 font-bold' : ''}`}
          >
            Features
          </span>
          <span 
            onClick={() => handleGlobalNavigation('pricing')} 
            className={`cursor-pointer transition-colors hover:text-white ${internalPage === 'pricing' ? 'text-teal-400 font-bold' : ''}`}
          >
            Pricing
          </span>
          <span 
            onClick={() => handleGlobalNavigation('blog')} 
            className={`cursor-pointer transition-colors hover:text-white ${location.pathname.startsWith('/blog') ? 'text-teal-400 font-bold' : ''}`}
          >
            Blog
          </span>
          <span 
            onClick={() => handleGlobalNavigation('docs')} 
            className={`cursor-pointer transition-colors hover:text-white ${location.pathname === '/docs' ? 'text-teal-400 font-bold' : ''}`}
          >
            Docs
          </span>
        </div>

        {/* 3. Right: Live Indicator & Identity Button */}
        <div className="flex items-center gap-3 md:gap-5 flex-shrink-0">
          
          {/* Live Badge (Hidden on mobile to save space) */}
          <span className="hidden xs:flex items-center text-[10px] font-bold text-green-500 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20 tracking-wider">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse mr-2"></span> LIVE
          </span>

          {/* Professional Login Button (The "Circle Icon" Style) */}
          <button 
            onClick={() => window.location.href = "https://account.airssecurity.com/login"}
            className="flex items-center gap-2 md:gap-3 pl-1 pr-3 md:pr-5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all group cursor-pointer active:scale-95"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
              <User className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
            </div>
            <span className="text-xs md:text-sm font-bold text-slate-300 group-hover:text-white transition-colors">
              Login
            </span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <main className="flex-grow">
        {children ? (
          <div className="animate-in fade-in duration-700">{children}</div>
        ) : (
          isRoot && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              {internalPage === 'home' ? (
                <>
                  {/* THE PROFESSIONAL 2-COLUMN HERO SECTION */}
                  <div className="w-full border-b border-white/5 bg-[#020617] py-16 md:py-24">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                      
                      {/* LEFT SIDE: Typography & Actions */}
                      <div className="space-y-8 text-left order-2 lg:order-1">
                        <h1 className="text-4xl md:text-6xl xl:text-7xl font-black text-white leading-[1.1] tracking-tight">
                          {settings.hero_headline || "The World's First Self-Healing AI Guardian."}
                        </h1>

                        <p className="text-lg md:text-xl text-slate-400 max-w-xl leading-relaxed">
                          {settings.hero_subtext || "Real-time system defense through AI-assisted monitoring, policy enforcement, and continuous learning."}
                        </p>

                        <div className="flex flex-wrap gap-4 pt-4">
                          <button 
                            onClick={() => window.location.href = "https://account.airssecurity.com/register"}
                            className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-500 shadow-xl shadow-blue-500/25 transition-all hover:scale-105 active:scale-95"
                          >
                            Get Protected
                          </button>
                          <button 
                             onClick={() => handleGlobalNavigation('product')}
                             className="px-8 py-4 rounded-xl font-bold text-white border border-white/10 hover:bg-white/5 transition-all"
                          >
                            View Technology
                          </button>
                        </div>
                      </div>

                      {/* RIGHT SIDE: Matrix Feed & Visuals */}
                      <div className="relative order-1 lg:order-2">
                        {/* Decorative Gradient Glow */}
                        <div className="absolute -inset-4 bg-blue-500/10 rounded-[2rem] blur-3xl"></div>
                        
                        <div className="relative space-y-4">
                          {/* Badge Tag */}
                          <div className="inline-flex items-center text-[10px] font-bold tracking-[0.2em] text-teal-400 uppercase bg-blue-500/10 px-4 py-2 rounded-full border border-blue-500/20">
                            Real-time system telemetry · Read-only · Policy-controlled
                          </div>

                          {/* Matrix HUD Container */}
                          <div className="p-1 rounded-2xl bg-gradient-to-b from-slate-700/50 to-slate-900/50 shadow-2xl border border-white/10">
                            <div className="bg-[#0a101f] rounded-xl overflow-hidden min-h-[350px]">
                              <MatrixFeed />
                            </div>
                          </div>

                          {/* Latest Alert Ticker */}
                          <div className="bg-white/5 backdrop-blur-md px-4 py-3 rounded-2xl flex items-center border border-white/10 shadow-lg">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-3 flex-shrink-0"></span>
                            <span className="text-[11px] font-mono text-red-300 tracking-wider uppercase">
                              LATEST: <span className="text-white font-bold ml-1">
                                {publicStats?.recent_threats?.[0] || "Scanning Neural Network..."}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                  <div className="py-20 md:py-24 px-6 max-w-7xl mx-auto">
                    <FeatureSection features={features} />
                  </div>
                  <div className="border-t border-white/5 bg-[#020617] relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(20,184,166,0.12),transparent_35%),radial-gradient(circle_at_80%_30%,rgba(139,92,246,0.10),transparent_32%)]"></div>

                    <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-6">
                        {[
                          {
                            label: 'Events Analyzed',
                            value: formatLargeNumber(publicStats?.total_analyzed) || '230M+',
                            desc: 'Continuously processed system telemetry.',
                            icon: Layers,
                            accent: 'text-teal-400',
                            border: 'border-teal-500/20',
                            glow: 'shadow-teal-500/10',
                          },
                          {
                            label: 'Threats Blocked',
                            value: publicStats?.threats_blocked?.toLocaleString() || '811',
                            desc: 'Policy-approved security responses.',
                            icon: ShieldCheck,
                            accent: 'text-purple-400',
                            border: 'border-purple-500/20',
                            glow: 'shadow-purple-500/10',
                          },
                          {
                            label: 'Active Agents',
                            value: publicStats?.active_sentinels?.toLocaleString() || '1',
                            desc: 'Endpoints currently protected by AIRS.',
                            icon: Users,
                            accent: 'text-blue-400',
                            border: 'border-blue-500/20',
                            glow: 'shadow-blue-500/10',
                          },
                          {
                            label: 'Pattern Rate',
                            value: `${publicStats?.ai_confidence || '89.5'}%`,
                            desc: 'Behavioral patterns learned and refined.',
                            icon: BrainCircuit,
                            accent: 'text-fuchsia-400',
                            border: 'border-fuchsia-500/20',
                            glow: 'shadow-fuchsia-500/10',
                          },
                        ].map((stat) => {
                          const Icon = stat.icon;

                          return (
                            <div
                              key={stat.label}
                              className={`group relative rounded-3xl border ${stat.border} bg-white/[0.035] p-6 md:p-7 min-h-[220px] overflow-hidden shadow-2xl ${stat.glow} hover:-translate-y-1 hover:bg-white/[0.055] transition-all duration-300`}
                            >
                              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-white/[0.08] to-transparent"></div>

                              <div className="relative flex items-start justify-between gap-4">
                                <div className={`w-14 h-14 rounded-2xl border ${stat.border} bg-slate-950/60 flex items-center justify-center`}>
                                  <Icon className={`w-7 h-7 ${stat.accent}`} />
                                </div>

                                <div className={`h-2 w-2 rounded-full ${stat.accent.replace('text-', 'bg-')} animate-pulse`}></div>
                              </div>

                              <div className="relative mt-8">
                                <h3 className={`font-black tracking-tight leading-none ${stat.accent} text-[clamp(2rem,4vw,3.4rem)] break-words`}>
                                  {stat.value}
                                </h3>

                                <p className="mt-5 text-sm font-black text-white uppercase tracking-[0.16em]">
                                  {stat.label}
                                </p>

                                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                  {stat.desc}
                                </p>

                                <div className={`mt-6 h-1 w-14 rounded-full ${stat.accent.replace('text-', 'bg-')}`}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
                ) : internalPage === 'pricing' ? (
                /* 3. RENDER PRICING SECTION */
                <div className="py-12 animate-in fade-in slide-in-from-bottom-8">
                  <PricingSection packages={publicPackages || []} />
                </div>
              ) : (
                <div className="py-12"><ProductShowcase sections={products} /></div>
              )}
            </div>
          )
        )}
      </main>
      
      {/* COOKIE CONSENT HERE */}
      <CookieConsent text={settings?.cookie_consent_text} />

      {/*  FOOTER CALLED HERE */}
      <SiteFooter settings={settings} onNavigate={handleGlobalNavigation} />
    </div>
  );
};